/**
 * @file 学んだ日を決め、暦 (直近の週 × 曜日) に並べる純関数を置く。
 *
 * @what: 読んだ時間 (StudyReadingSession)、取り組みの記録、学びメモ、復習カードの結果、AI に聞いた記録から
 *   日ごとの活動をまとめ、「学んだ日」かどうかを判定する。学んだ日は、実際に操作していた時間が
 *   LEARNED_DAY_MIN_SECONDS 以上か、その日に何かの記録 (手書きの記録・メモ・復習・AI に聞く) があるかで決まる。
 *   その日にさわった本 (資料 id) も、どの記録からも集める。暦は今日を右端にした1行の日付の並びである。
 * @why: Kindle は開けば数えるが、ここでは操作の実態がある日だけを数える (代表の判断、2026-09-13)。紙の本の
 *   学習は手書きの記録でしか取れないので、自動の計測と手書きの記録を同じ物差しに乗せる。判定を保存せず
 *   毎回計算するのは、記録が消えたり直されたりしたときに数え直せるためである。
 * @failure: 副作用を持たない純関数である。時刻の文字列が読めない記録は飛ばす。
 */

import type { StudyAskRecordSummary } from "../api/askRecords";
import type { StudyNote } from "../api/notes";
import type { StudyProgressLog } from "../api/plans";
import type { StudyReadingSession } from "../api/readingSessions";
import type { StudyReviewCard } from "../api/reviewCards";
import { addDaysLocal } from "../api/dates";
import { parseSourceStudyLog } from "./sourceStudyLog";

/** 学んだ日とみなす、実際に操作していた秒数の下限である。 */
export const LEARNED_DAY_MIN_SECONDS = 300;

/** ページを「読んだ」と数える滞在の秒数 (サーバの StudyReadingPageReadMinSeconds と同じ値)。 */
export const PAGE_READ_MIN_SECONDS = 15;

/** 記録を読む期間の長さ (週)。暦に出す日数は画面の幅で決まり、この期間を超えない。 */
export const STUDY_DAYS_WEEKS = 12;

/** 記録の種類 (その日に何があったか)。 */
export type StudyDayMark = "reading" | "log" | "note" | "review" | "ask";

/** 1つの資料についての、その日の活動である。 */
export interface StudyDaySource {
  sourceId: string;
  /** 実際に操作していた秒数 (読んだ時間から)。 */
  seconds: number;
  /** 読んだページ (滞在が PAGE_READ_MIN_SECONDS 以上) の数である。 */
  pagesRead: number;
}

export interface StudyDay {
  date: string;
  /** 実際に操作していた秒数の合計 (読んだ時間から)。 */
  seconds: number;
  /** 読んだページの数の合計である。 */
  pagesRead: number;
  /** 手書きの記録の分数の合計である。 */
  logMinutes: number;
  /** 資料ごとの活動 (読んだ時間と、資料への記録)。 */
  sources: Map<string, StudyDaySource>;
  /** その日にあったことの種類である。 */
  marks: Set<StudyDayMark>;
}

/** ISO の時刻を端末のローカル日付 (YYYY-MM-DD) にする。読めなければ null。 */
export function localDateOf(iso: string): string | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface StudyDaysInput {
  sessions: readonly StudyReadingSession[];
  /** まなぶ人の記録 (絞り込み済み)。 */
  logs: readonly StudyProgressLog[];
  /** まなぶ人のメモ (絞り込み済み)。 */
  notes: readonly StudyNote[];
  /** まなぶ人の復習カード (絞り込み済み)。 */
  cards: readonly StudyReviewCard[];
  /** まなぶ人の AI に聞いた記録である。 */
  askRecords: readonly StudyAskRecordSummary[];
}

function dayOf(map: Map<string, StudyDay>, date: string): StudyDay {
  let day = map.get(date);
  if (!day) {
    day = { date, seconds: 0, pagesRead: 0, logMinutes: 0, sources: new Map(), marks: new Set() };
    map.set(date, day);
  }
  return day;
}

function sourceOf(day: StudyDay, sourceId: string): StudyDaySource {
  let s = day.sources.get(sourceId);
  if (!s) {
    s = { sourceId, seconds: 0, pagesRead: 0 };
    day.sources.set(sourceId, s);
  }
  return s;
}

/** 日ごとの活動をまとめる (日付 → StudyDay)。 */
export function buildStudyDays(input: StudyDaysInput): Map<string, StudyDay> {
  const days = new Map<string, StudyDay>();
  for (const s of input.sessions) {
    const day = dayOf(days, s.date);
    const src = sourceOf(day, s.source_id);
    src.seconds += s.active_seconds;
    day.seconds += s.active_seconds;
    const read = Object.values(s.pages).filter((sec) => sec >= PAGE_READ_MIN_SECONDS).length;
    src.pagesRead += read;
    day.pagesRead += read;
    if (s.active_seconds > 0) day.marks.add("reading");
  }
  for (const log of input.logs) {
    const day = dayOf(days, log.date);
    day.marks.add("log");
    if (log.minutes && log.minutes > 0) day.logMinutes += log.minutes;
    const detail = parseSourceStudyLog(log);
    if (detail !== null) sourceOf(day, detail.source_id);
  }
  for (const n of input.notes) {
    for (const iso of [n.created_at, n.updated_at]) {
      const date = localDateOf(iso);
      if (date === null) continue;
      const day = dayOf(days, date);
      day.marks.add("note");
      // メモが本のページに紐づいていれば、その本をさわった本に数える
      for (const ref of n.refs ?? []) if (ref.kind === "source") sourceOf(day, ref.source_id);
    }
  }
  for (const c of input.cards) {
    for (const r of c.reviews ?? []) {
      const day = dayOf(days, r.date);
      day.marks.add("review");
      if (c.ref?.source_id) sourceOf(day, c.ref.source_id);
    }
  }
  for (const r of input.askRecords) {
    for (const iso of [r.created_at, r.updated_at]) {
      const date = localDateOf(iso);
      if (date === null) continue;
      const day = dayOf(days, date);
      day.marks.add("ask");
      for (const t of r.targets) if (t.source_id) sourceOf(day, t.source_id);
    }
  }
  return days;
}

/** 学んだ日かどうかを返す。 */
export function isLearnedDay(day: StudyDay | undefined): boolean {
  if (!day) return false;
  if (day.seconds >= LEARNED_DAY_MIN_SECONDS) return true;
  return day.marks.has("log") || day.marks.has("note") || day.marks.has("review") || day.marks.has("ask");
}

/** 月曜始まりで、その日を含む週の月曜を返す。 */
export function weekStartOf(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const t = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  // getDay は日曜が0なので、月曜を0にずらす
  const offset = (t.getDay() + 6) % 7;
  return addDaysLocal(date, -offset);
}

/**
 * 今日を右端にして、直近 count 日の日付を昇順で返す。
 *
 * @what: 暦は1行なので、出す日数は画面の幅で決まる (components/StudyDaysCalendar.tsx が測る)。
 * @why: 週 × 曜日の2次元にすると、狭い画面では横に溢れて隠れる日ができる。画面の幅に収まるぶんだけを
 *   1行で出す (代表の判断、2026-09-13)。
 */
export function studyDayList(today: string, count: number): string[] {
  const n = Math.max(1, Math.floor(count));
  return Array.from({ length: n }, (_, i) => addDaysLocal(today, i - (n - 1)));
}

/** マス1つの目安の幅 (px)。この幅で何日ぶん並ぶかを決める。 */
export const STUDY_DAYS_CELL_PX = 16;

/** マスの間 (px)。study.css の gap と合わせる。 */
export const STUDY_DAYS_GAP_PX = 3;

/** 幅を測れないとき (テスト・非表示のとき) に出す日数である。 */
export const STUDY_DAYS_FALLBACK_COUNT = 28;

/** 出す日数の下限である。 */
export const STUDY_DAYS_MIN_COUNT = 7;

/** 行の幅 (px) から、並べる日数を出す。max は読み込んだ期間の日数である。 */
export function dayCountForWidth(width: number, max: number): number {
  const fit = Math.floor((width + STUDY_DAYS_GAP_PX) / (STUDY_DAYS_CELL_PX + STUDY_DAYS_GAP_PX));
  return Math.max(STUDY_DAYS_MIN_COUNT, Math.min(max, fit));
}
